// File picker + uploader. Runs the input file through the image-processing
// pipeline (resize → WebP → strip EXIF) before the presigned-URL PUT.

import { useRef, useState } from "react";
import { uploadFile, type UploadPurpose } from "../api/uploads";
import { ImagePipelineError, processImage } from "../lib/imagePipeline";

interface Props {
  label: string;
  purpose: UploadPurpose;
  value: string | null | undefined;
  onChange: (publicUrl: string | null) => void;
  accept?: string;
  helper?: string;
}

type Step = "processing" | "uploading" | null;

export function ImageUpload({
  label,
  purpose,
  value,
  onChange,
  accept = "image/jpeg,image/png,image/webp",
  helper,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>(null);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setStep("processing");
    try {
      const processed = await processImage(file, purpose);
      setStep("uploading");
      const publicUrl = await uploadFile(processed, purpose);
      onChange(publicUrl);
    } catch (err) {
      if (err instanceof ImagePipelineError) {
        setError(err.userMessage);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Upload failed");
      }
      console.error("imagePipeline / upload failed", err);
    } finally {
      setStep(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const busy = step !== null;
  const buttonLabel = step === "processing"
    ? "Processing…"
    : step === "uploading"
      ? "Uploading…"
      : value
        ? "Replace"
        : "Upload";

  return (
    <div>
      <span className="field-label">{label}</span>
      <div className="grid grid-cols-[88px_1fr_auto] items-center gap-3 rounded border border-dashed border-rule bg-cream-card p-3">
        <div className="size-22 grid place-items-center rounded bg-stone overflow-hidden">
          {value ? (
            <img src={value} alt="" className="size-full object-cover" />
          ) : (
            <span className="font-display text-2xl text-ink-mute italic">{label.charAt(0)}</span>
          )}
        </div>
        <div className="min-w-0 text-xs">
          {value ? (
            <>
              <div className="flex items-center gap-1.5 font-mono text-moss">
                <span aria-hidden="true" className="inline-block size-1.5 rounded-full bg-moss" />
                Uploaded
              </div>
              <div
                className="mt-1 truncate font-mono text-ink-mute"
                title={value}
              >
                {(value.split("/").pop() ?? "").slice(0, 8)}…{value.slice(-4)}
              </div>
            </>
          ) : (
            <>
              <div className="font-mono text-ink-mute">No image yet</div>
              {helper && <div className="mt-1 text-ink-mute">{helper}</div>}
            </>
          )}
          {error && <div className="mt-1 text-plum">{error}</div>}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm whitespace-nowrap"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {buttonLabel}
          </button>
          {value && !busy && (
            <button
              type="button"
              className="btn btn-ghost btn-sm text-plum"
              onClick={() => onChange(null)}
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onPick}
          className="hidden"
        />
      </div>
    </div>
  );
}
